import { makeObservable, computed, observable, action, reaction, runInAction } from 'mobx';
import { Field, FieldFactory } from './field/FieldFactory';
// import { PageStatusType } from '@utils/type'
import { FormData, ValueObj } from './data/FormData'
import { FormValidator, ValidateFields } from './FormValidator';
import { cloneDeep } from 'lodash'
import { HOOKS } from './field/BaseField';

type StoreField = {
  fieldAlias: string
  defaultValue?: ValueObj
  dataSourceAlias?: string
  alias: string // 数据源字段别名 field: xxx.name, alias: xxx_name
  bizType: string
  required?: boolean // 必填
  readOnly?: boolean // 只读
  visible?: boolean // 可见
  virtualField?: boolean
}

export interface Meta {
  defaultValue?: ValueObj
  bizType?: string // 数据源字段类型
  caption?: string
  controlType?: string // 组件类型？
  required?: boolean // 必填
  readOnly?: boolean // 只读
  visible?: boolean // 可见
  disabled?: boolean
}

export type StoreMeta = {
  fieldsArr: StoreField[]
}

export interface FieldDesc extends Meta {
  fieldAlias: string
  stateType?: string
  characterId?: string
  field?: string
  data?: any
  hooks?: HOOKS
  isMasterOrg?: boolean
  ownDomain?: string
  refEntityUri?: string
  refReturnArr?: any
}

const PageStatusType = {
  INIT: 'init',
  EDIT: 'edit',
  BROWSE: 'browse',
  ADD: 'add'
}

enum BillStatusType {
  OPEN = '0',
  AUDIT = '1',
  AUDITED = '2',
  STOP = '3',
  DisapproveDraft = '4'
}

/**
 * FormStore的功能
 * 1. 存储表单的数据，提供写入和读取的方法，不包含子表数据
 * 2. 管理表单状态，提供Computed控制只读，可编辑
 * 3. 数据校验
 * 3.1 必输校验，校验规则从DataSource上读取
 */
export class FormStore extends FormData {
  fields: Map<string, Field>
  fieldFactory: FieldFactory
  formValidator: FormValidator
  uiState: string
  rootStore: any
  workFlowStore: any
  storeType: string = 'FormStore'
  showRequiredFields: boolean
  hooks: any
  workFlowHooks: any
  characterEffects?: {
    [key: string]: string[]
  }

  // 主组织id字段
  masterOrgField?: string
  // 主组织name字段
  masterOrgNameField?: string
  // 主组织信息字段，add接口使用
  masterOrgInfo?: object
  // 主组织id + name的值拼接，主组织切换时监测此值进行ui模板切换
  masterOrgValue?: string
  // 交易类型id字段
  transTypeField?: string
  // 交易类型name字段
  transTypeNameField?: string
  // 交易类型id + name的值拼接，交易类型切换时监测此值进行ui模板切换
  transTypeValue?: string
  constructor(storeMeta?: StoreMeta | any, rootStore?: any) {
    super(storeMeta)
    this.rootStore = rootStore;
    this.fields = observable.map()
    this.fieldFactory = new FieldFactory(this)
    this.formValidator = new FormValidator()
    this.uiState = PageStatusType.INIT
    this.showRequiredFields = false
    this.masterOrgField = ''
    this.masterOrgNameField = ''
    this.masterOrgInfo = {}
    this.masterOrgValue = ''
    this.transTypeField = ''
    this.transTypeNameField = ''
    this.transTypeValue = ''
    this.hooks = {}
    this.workFlowHooks = {}

    makeObservable(this, {
      uiState: observable,
      showRequiredFields: observable,
      set: action.bound,
      hasErr: computed,
      readOnly: computed,
      isBrowse: computed,
      isEdit: computed,
      isAdd: computed,
      setUiState: action.bound,
      validate: action.bound,
      clear: action.bound,
      reset: action.bound,
      add: action.bound,
      cancelEdit: action.bound,
      masterOrgField: observable,
      masterOrgFieldValue: computed,
      masterOrgNameField: observable,
      masterOrgValue: observable,
      transTypeField: observable,
      transTypeNameField: observable,
      transTypeValue: observable
    })

    storeMeta?.fieldsArr.forEach((field: any) => {
      // formStore里不支持设置defaultValue，跟组件协议里设置defaultValue会重复，导致组件设置defaultValue不生效
      delete field.defaultValue
      this.set(field.alias, field)
    })

    this.hooks.afterSetValue = () => {
      // 如果有特征副作用, 且处于浏览态, 请求接口
      for (const referField in this.characterEffects) {
        const characterFields = this.characterEffects[referField]
        const id = this.getValue(referField)
        const types = characterFields.map(characterField => {
          return this.get(characterField)?.type
        })
        const characterStore = this.rootStore.pageStore.characterStore
        characterStore.getCharacterDesc([id], types)
      }
    }

    reaction(() => this.isEdit, (isEdit: any) => {
      if (isEdit) this.dirtyData = cloneDeep(this.data)
    })
  }

  /**
   * 获取表单字段信息
   * @function get
   * @param {string} fieldAlias
   * @returns {Field}
   */
  get(fieldKey: string) : any {
    return this.fields.get(fieldKey)
  }

  /**
   * 记录表单字段表述信息
   * @function set
   * @param {string} fieldAlias
   * @param {FieldDesc[]} fieldDesc
   */
  set(fieldAlias: string, fieldDesc: FieldDesc) {
    // TODO: 合并再次创建时的属性？
    try {
      // 创建某个field失败时，不阻断formStore的创建
      const field = this.get(fieldAlias)
      // 老数据meta 数据上会配置一个错误的field, 在合并数据时候需要过滤掉
      const mergedDesc = field ? { defaultValue: field.defaultValue, hooks: field.hooks, stateType: field.stateType, ...fieldDesc, field: field.field, fieldAlias } : { ...fieldDesc, fieldAlias }
      if (mergedDesc.isMasterOrg) {
        this.masterOrgField = mergedDesc.fieldAlias
        this.masterOrgNameField = `${mergedDesc.fieldAlias}_name`
        this.masterOrgInfo = {
          domain: mergedDesc.ownDomain,
          fullName: mergedDesc.refEntityUri,
          fieldArr: Object.keys(JSON.parse(mergedDesc.refReturnArr)).map(field => { return { field } })
        }
      }
      this.fields.set(fieldAlias, this.fieldFactory.createField(mergedDesc))
    } catch (e) {
      console.log(`创建字段${fieldAlias}错误`, e)
    }
  }

  /**
   * 表单中是否存在校验未通过的字段
   * @computed hasErr
   */
  get hasErr() {
    let hasErr = false
    for (const [field] of this.fields) {
      // @ts-ignore
      if (field.hasErr) {
        hasErr = true
        break
      }
    }
    return hasErr
  }

  /**
   * 表单清空
   * @function clear
  */
  clear() {
    this.fields.forEach(field => field.clear())
  }

  // 清空表单&&将表单状态置为init
  reset() {
    this.clear()
    this.setUiState(PageStatusType.INIT)
  }

  // 清空表单&&将表单状态置为add
  add() {
    // this.clear()
    this.setUiState(PageStatusType.ADD)
  }

  // 取消编辑
  cancelEdit() {
    runInAction(() => {
      this.clear()
      // @ts-ignore
      this.setValue(this.dirtyData)
      this.dirtyData = {}
      this.setUiState(PageStatusType.BROWSE)
    })
  }

  /**
   * 校验表单每一项，收集错误信息
   * @function validate
   * @returns {string[]} 错误信息
   */
  validate() {
    const validateFields: ValidateFields = []
    this.fields.forEach((field: Field, fieldAlias: string) => {
      field.validate()
      validateFields.push({ fieldName: field.fieldName, fieldAlias, value: field.getValue(), validators: field.validators })
    })
    return this.formValidator.validate(validateFields)
  }

  /**
   * 设置表单状态
   * @function setUiState
   * @param {string} uiState 表单状态
   */
  setUiState(uiState: string) {
    this.uiState = uiState
  }

  /**
   * 设置表单字段的显示隐藏
   * @function setVisible
   * @param {string} fieldAlias
   * @param {boolean} visible
   */
  setVisible(fieldAlias: string, visible: boolean) {
    this.fields.get(fieldAlias)?.setVisible(visible)
  }

  /**
   * 设置表单字段的必填
   * @function setRequired
   * @param {string} fieldAlias
   * @param {boolean} required
   */
  setRequired(fieldAlias: string, required: boolean) {
    this.fields.get(fieldAlias)?.setRequired(required)
  }

  // 设置交易类型相关字段
  setTransTypeField = (transTypeField: string) => {
    this.transTypeField = transTypeField
    this.transTypeNameField = `${transTypeField}_name`
  }

  // 设置主组织value
  setMasterOrgValue = (id: string, name: string) => {
    this.masterOrgValue = (id || '') + (name || '')
  }

  // 设置交易类型value
  setTransTypeValue = (id: string, name: string) => {
    this.transTypeValue = (id || '') + (name || '')
  }

  toggleShowRequiredFields = () => {
    this.showRequiredFields = !this.showRequiredFields
  }

  getFieldsString = (name: string) => {
    const fieldString: string[] = []
    this.fields.forEach((value) => {
      if (!name || value.fieldName === name) {
        fieldString.push(`${value.fieldName}_${value.fieldAlias}`)
      }
    })
    return fieldString
  }

  get readOnly() {
    return this.isBrowse;
  }

  get isBrowse() {
    return this.uiState === PageStatusType.BROWSE;
  }

  get isEdit() {
    // 保存并新增 保存 取消
    return this.uiState === PageStatusType.ADD || this.uiState === PageStatusType.EDIT || this.uiState === PageStatusType.INIT;
  }

  get isUnEdit() {
    // 启用 停用 上一页 下一页 打印 复制
    return !this.isEdit
  }

  get isAdd() {
    return this.uiState === PageStatusType.ADD
  }

  get systemFieldVisible() {
    return this.isBrowse
  }

  get systemFieldReadonly() {
    return true
  }

  get btnAuditVisible() {
    // 审核
    return (!this.procinstId) && this.isBrowse && this.verifyState === BillStatusType.AUDIT
  }

  get btnWorkFlowVisible() {
    // 审批流
    const isAudit = this.verifyState === BillStatusType.AUDIT && this.isBrowse
    const isAudited = this.verifyState === BillStatusType.AUDITED && this.isBrowse
    const isStop = this.verifyState === BillStatusType.STOP && this.isBrowse // 产品说终止态又要审批
    return (!!this.procinstId) && (isAudited || isAudit || isStop)
  }

  get btnUnAuditVisible() {
    // 弃审
    const isAudited = this.verifyState === BillStatusType.AUDITED && this.isBrowse
    return (!this.procinstId) && isAudited
  }

  get btnEditVisible() {
    // 编辑
    const isBrowse = !this.verifyState && this.isBrowse
    const isVerifyState = (this.verifyState === BillStatusType.OPEN || this.verifyState === BillStatusType.STOP || this.verifyState === BillStatusType.DisapproveDraft) && this.isBrowse
    const isAudit = this.verifyState === BillStatusType.AUDIT && (!!this.procinstId) && this.isBrowse && (!!this.workFlowStore.currentTaskAuth)
    return isBrowse || isVerifyState || isAudit
  }

  get btnEditWithApproveStatus() {
    // 编辑按钮审批中也显示--中核王勇老师提
    const isBrowse = !this.verifyState && this.isBrowse
    const isVerifyState = (this.verifyState === BillStatusType.OPEN || this.verifyState === BillStatusType.STOP || this.verifyState === BillStatusType.DisapproveDraft) && this.isBrowse
    const isAudit = this.verifyState === BillStatusType.AUDIT && this.isBrowse
    return isBrowse || isVerifyState || isAudit
  }

  get btnSubmitVisible() {
    // 提交
    const isBrowse = !this.verifyState && this.isBrowse
    const isVerifyState = (this.verifyState === BillStatusType.OPEN || this.verifyState === BillStatusType.DisapproveDraft) && this.isBrowse
    return isBrowse || isVerifyState
  }

  get btnUnSubmitVisible() {
    // 撤回
    const isBrowse = !this.verifyState && this.isBrowse
    const isAudit = this.verifyState === BillStatusType.AUDIT && this.isBrowse
    return isBrowse || isAudit
  }

  get btnDeleteVisible() {
    // 删除
    const isBrowse = !this.verifyState && this.isBrowse
    const isOpen = this.verifyState === BillStatusType.OPEN && this.isBrowse
    const isStop = this.verifyState === BillStatusType.STOP && this.isBrowse
    const isDisapproveDraft = this.verifyState === BillStatusType.DisapproveDraft && this.isBrowse
    return isBrowse || isOpen || isStop || isDisapproveDraft
  }

  /**
   * 主组织判断按钮显隐
   * @param btnAlias 按钮别名
   * @returns 经主组织判断后是否显示
   */
  get isMasterOrgField() {
    return !this.masterOrgFieldValue && this.isEdit
  }

  get verifyState() {
    return this.getValue('verifyState')?.toString()
  }

  get procinstId() {
    return this.getValue('procinstId')?.toString()
  }

  get masterOrgFieldValue() {
    return this.getValue(this.masterOrgField)
  }


  getWorkFlowHooks = () => {
    return this.workFlowHooks
  }

  setWorkFlowHooks = (key: string, fn: Function) => {
    if (key) {
      this.workFlowHooks[key] = fn
    }
  }
}
